import { useState, useRef, useEffect } from "react";
import { message } from "antd";
import { STATUS } from "@/utils/enum";
import { getTrimPackageFromVinData, handleAdminVinOverride } from "@/utils/utils";
import { getVINData, getVinDataByVehicle } from "@/services/vehicles";

const useVinDecoder = ({ vin, make, model, formRef, isAdmin }) => {
  const lastDecodedRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [list, setList] = useState([]);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Determine if decoding should proceed
    const shouldDecode =
      vin?.length === 17 || // vin provided
      (isAdmin && make && model); // make and model provided for admin

    if (!shouldDecode) return;

    let isMounted = true;

    const isVinComplete = vin?.length === 17;

    const key = isVinComplete ? vin : `${make}-${model}`;

    // avoid to decode the same vin twice
    if (lastDecodedRef.current === key) return;

    const decode = async () => {
      const textMessage = isVinComplete ? "Decoding VIN..." : "Searching make and model...";
      const hide = message.loading(textMessage, 0);

      try {
        lastDecodedRef.current = key;
        setStatus(STATUS.LOADING);
        setError(null);

        const data = isVinComplete ? await getVINData(vin) : await getVinDataByVehicle(make, model);

        hide();

        if (isMounted) {
          // no data found
          if (!data || data?.length === 0) {
            setList([]);
            setSelected(null);
            setStatus(STATUS.ERROR);
            setError("No data found about this VIN. Please enter the vehicle information manually.");
            return;
          }

          setStatus(STATUS.SUCCESS);
          message.success("Decoded successfully!");

          // only one result
          if (data?.length === 1) {
            const vehicleData = {
              ...data[0],
              trimPackageName: getTrimPackageFromVinData(data[0]),
              vin: data[0].vin ?? vin,
            };
            setSelected(vehicleData);
            formRef?.setFieldsValue(vehicleData); // auto-fill the form
            setList([]);
            return;
          }
          setList(data);
          setSelected(null);
          formRef?.setFieldsValue({ trim: null, trimPackageName: null }); // clean old selection
        }
      } catch (err) {
        if (isMounted) {
          setList([]);
          setSelected(null);
          setStatus(STATUS.ERROR);
          setError("No data found about this VIN. Please enter the vehicle information manually.");
        }
      }
    };

    decode();

    return () => {
      isMounted = false;
    };
  }, [vin, make, model, isAdmin, formRef]);

  // handle admin vin override
  useEffect(() => {
    if (status === STATUS.ERROR && isAdmin) {
      handleAdminVinOverride()
        .then(() => {
          setStatus(STATUS.SUCCESS); // override error
        })
        .catch(() => {
          formRef?.setFields([
            {
              name: "vin",
              errors: ["Invalid VIN"],
            },
          ]);
        });
    }

    /// non admin vin override
    if (status === STATUS.ERROR && !isAdmin) {
      formRef?.setFields([
        {
          name: "vin",
          errors: ["Invalid VIN"],
        },
      ]);
    }
  }, [isAdmin, status, formRef]);

  const handleSelect = index => {
    const item = list[index];

    setSelected({
      ...item,
      trim: index,
      trimPackageName: getTrimPackageFromVinData(item),
      vin: item.vin ?? vin,
    });

    formRef?.setFieldsValue({
      ...item,
      trim: index,
      trimPackageName: getTrimPackageFromVinData(item),
      vin: item.vin ?? vin,
    }); // update form with selected data

    setIsOpen(false);
  };

  const handleOpenChange = open => {
    setIsOpen(open);
  };

  // When list changes and has multiple items, open the dropdown
  if (list.length > 1 && !selected && !isOpen) {
    setIsOpen(true);
  }

  return {
    data: selected,
    status,
    error,
    list,
    handleSelect,
    isOpen,
    handleOpenChange,
  };
};

export default useVinDecoder;
